import { useDataProvider, useGetList, useNotify } from "ra-core";
import { useMemo, useState } from "react";
import { Plus, Trash2, Users, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { runReport } from "../reports/ReportBuilder";
import { useReportFieldDefs } from "../reports/useReportFields";
import { EmptyState } from "../misc/EmptyState";
import {
  useConfigurationContext,
  useConfigurationUpdater,
  type ConfigurationContextValue,
} from "../root/ConfigurationContext";
import type { CrmDataProvider } from "../providers/types";
import type { Contact, ContactListDef } from "../types";

/**
 * Lists & segments - named, dynamic contact lists built from the same layered
 * filter conditions as the report builder. Lists are the audiences for
 * sequences and campaigns.
 */

let uid = Date.now() % 100000;
const nextUid = () => ++uid;

const OPS: Record<string, { id: string; name: string }[]> = {
  enum: [
    { id: "is", name: "is" },
    { id: "is_not", name: "is not" },
  ],
  owner: [
    { id: "is", name: "is" },
    { id: "is_not", name: "is not" },
  ],
  date: [
    { id: "last_days", name: "in last N days" },
    { id: "before", name: "before" },
    { id: "after", name: "after" },
  ],
  text: [
    { id: "contains", name: "contains" },
    { id: "eq", name: "equals" },
  ],
};

/**
 * Lists share the full report field catalog - lifecycle stage, lead score
 * and grade, touch channels, campaign engagement (opened / clicked / didn't
 * open a given sent campaign), tags, newsletter - so "opened but didn't
 * click campaign X" is a two-condition list. Same defs power campaign
 * audiences and sequence enrollment automatically.
 */
export const useContactFieldDefs = () => useReportFieldDefs("contacts");

/**
 * Resolve members for any list on demand - used by the campaign and
 * sequence list views, where each row has its own audience.
 */
export const useMembersResolver = () => {
  const { data: contacts = [] } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 1000 },
  });
  const fields = useContactFieldDefs();
  return useMemo(
    () =>
      (list: ContactListDef | null | undefined): Contact[] => {
        if (!list) return [];
        const result = runReport(
          contacts,
          {
            object: "contacts",
            measure: "count",
            groupBy: "",
            logic: list.logic,
            filters: list.filters,
          },
          fields as never,
        );
        return result.rows as Contact[];
      },
    [contacts, fields],
  );
};

export const useListMembers = (list: ContactListDef | null) => {
  const { data: contacts = [] } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 1000 },
  });
  const fields = useContactFieldDefs();
  return useMemo(() => {
    if (!list) return [];
    const result = runReport(
      contacts,
      {
        object: "contacts",
        measure: "count",
        groupBy: "",
        logic: list.logic,
        filters: list.filters,
      },
      fields as any,
    );
    return result.rows as Contact[];
  }, [contacts, list, fields]);
};

export const ListsTab = () => {
  const config = useConfigurationContext();
  const updateConfig = useConfigurationUpdater();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const lists = config.contactLists ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(lists[0]?.id ?? null);
  const list = lists.find((l) => l.id === selectedId) ?? null;
  const fields = useContactFieldDefs();
  const members = useListMembers(list);

  // Local state updates instantly; the round-trip to the tenant's own
  // configuration row fires immediately for discrete actions (add/remove a
  // condition, pick a list, create/delete a list) and on blur for free-typed
  // fields (list name, a filter's text value), so keystrokes don't spam the
  // network. Without this, lists only ever lived in the browser's local
  // store - never actually reaching the database.
  const persist = async (next: ConfigurationContextValue) => {
    try {
      await dataProvider.updateConfiguration(next);
    } catch {
      notify("Couldn't save that - you may need admin access to update lists.", {
        type: "error",
      });
    }
  };

  const saveLists = (next: ContactListDef[], opts: { persistNow?: boolean } = {}) => {
    const nextConfig = { ...config, contactLists: next };
    updateConfig(nextConfig);
    if (opts.persistNow !== false) void persist(nextConfig);
  };
  const flushLists = () => void persist({ ...config, contactLists: lists });
  const patchList = (patch: Partial<ContactListDef>, opts?: { persistNow?: boolean }) => {
    if (!list) return;
    saveLists(
      lists.map((l) => (l.id === list.id ? { ...l, ...patch } : l)),
      opts,
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        {lists.map((l) => (
          <Button
            key={l.id}
            variant={l.id === selectedId ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedId(l.id)}
          >
            {l.name}
          </Button>
        ))}
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const id = `list-${nextUid()}`;
            saveLists([...lists, { id, name: "New list", logic: "all", filters: [] }]);
            setSelectedId(id);
          }}
        >
          <Plus className="w-4 h-4" /> New list
        </Button>
      </div>

      {list && (
        <div className="grid lg:grid-cols-2 gap-4">
          <Card>
            <CardContent className="p-4 flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Input
                  value={list.name}
                  onChange={(e) => patchList({ name: e.target.value }, { persistNow: false })}
                  onBlur={flushLists}
                  className="font-medium"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Delete list"
                  onClick={() => {
                    saveLists(lists.filter((l) => l.id !== list.id));
                    setSelectedId(lists.find((l) => l.id !== list.id)?.id ?? null);
                  }}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Contacts matching</span>
                <Select
                  value={list.logic}
                  onValueChange={(logic: "all" | "any") => patchList({ logic })}
                >
                  <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">ALL</SelectItem>
                    <SelectItem value="any">ANY</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const first = fields[0]!;
                    patchList({
                      filters: [
                        ...list.filters,
                        { id: nextUid(), field: first.key, op: OPS[first.type]![0]!.id, value: "" },
                      ],
                    });
                  }}
                >
                  <Plus className="w-4 h-4" /> Add condition
                </Button>
              </div>
              {list.filters.map((f) => {
                const field = fields.find((x) => x.key === f.field) ?? fields[0]!;
                const ops = OPS[field.type]!;
                const choices = "choices" in field ? field.choices : undefined;
                return (
                  <div key={f.id} className="flex flex-wrap items-center gap-2">
                    <Select
                      value={f.field}
                      onValueChange={(key) => {
                        const nf = fields.find((x) => x.key === key)!;
                        patchList({
                          filters: list.filters.map((x) =>
                            x.id === f.id
                              ? { ...x, field: key, op: OPS[nf.type]![0]!.id, value: "" }
                              : x,
                          ),
                        });
                      }}
                    >
                      <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {fields.map((x) => (
                          <SelectItem key={x.key} value={x.key}>{x.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={f.op}
                      onValueChange={(op) =>
                        patchList({
                          filters: list.filters.map((x) => (x.id === f.id ? { ...x, op } : x)),
                        })
                      }
                    >
                      <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ops.map((o) => (
                          <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {choices ? (
                      <Select
                        value={f.value}
                        onValueChange={(value) =>
                          patchList({
                            filters: list.filters.map((x) =>
                              x.id === f.id ? { ...x, value } : x,
                            ),
                          })
                        }
                      >
                        <SelectTrigger className="w-40"><SelectValue placeholder="choose…" /></SelectTrigger>
                        <SelectContent>
                          {choices.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        className="w-36"
                        type={f.op === "last_days" ? "number" : field.type === "date" ? "date" : "text"}
                        placeholder={f.op === "last_days" ? "days" : "value"}
                        value={f.value}
                        onChange={(e) =>
                          patchList(
                            {
                              filters: list.filters.map((x) =>
                                x.id === f.id ? { ...x, value: e.target.value } : x,
                              ),
                            },
                            { persistNow: false },
                          )
                        }
                        onBlur={flushLists}
                      />
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() =>
                        patchList({ filters: list.filters.filter((x) => x.id !== f.id) })
                      }
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <p className="text-sm font-medium mb-2 tabular-nums">
                {members.length} contact{members.length === 1 ? "" : "s"} in this list
              </p>
              <div className="flex flex-col gap-1">
                {members.slice(0, 12).map((c) => (
                  <p key={c.id} className="text-sm">
                    {c.first_name} {c.last_name}
                    <span className="text-muted-foreground"> · {c.title}</span>
                  </p>
                ))}
                {members.length > 12 && (
                  <p className="text-xs text-muted-foreground">
                    + {members.length - 12} more
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      {!list && (
        <EmptyState
          icon={Users}
          title="Segments that stay fresh"
          body="A list is a saved set of conditions - lifecycle stage, lead grade, who clicked a campaign - that re-evaluates live. Lists become the audience for campaigns and sequence enrollment."
          primaryLabel="Start with 'Hot A-grade leads'"
          onPrimary={() => {
            const id = `list-${nextUid()}`;
            saveLists([
              ...lists,
              {
                id,
                name: "Hot A-grade leads",
                logic: "all",
                filters: [
                  { id: 1, field: "lead_grade", op: "is", value: "A" },
                  { id: 2, field: "status", op: "is", value: "hot" },
                ],
              },
            ]);
            setSelectedId(id);
          }}
          secondaryLabel="Start blank"
          onSecondary={() => {
            const id = `list-${nextUid()}`;
            saveLists([
              ...lists,
              { id, name: "New list", logic: "all", filters: [] },
            ]);
            setSelectedId(id);
          }}
        />
      )}
    </div>
  );
};
